import { alpha, Box, Icon, Typography } from "@mui/material";

import FernieFace from "~/components/fernie/FernieFace";
import FernieMicro from "~/components/fernie/FernieMicro";
import FernieName from "~/components/fernie/FernieName";
import { governMark } from "~/helpers/aiChat/fernieFaceGovernance";

export type FernieHeaderAccent = "error" | "primary" | "success";

const HEADER_MARK_SIZE = 20;

const HeaderMark = () => {
	const decision = governMark({ size: HEADER_MARK_SIZE, surface: "panel", tone: "confirm" });
	if (decision.mark === "none") return null;
	if (decision.mark === "face")
		return <FernieFace decorative size={HEADER_MARK_SIZE} state={decision.grin ? "rest" : "straight"} />;
	if (decision.mark === "micro") return <FernieMicro decorative size={HEADER_MARK_SIZE} variant={decision.variant} />;
	return <FernieMicro decorative size={HEADER_MARK_SIZE} variant="eyes" />;
};

interface FernieCardHeaderProps {
	accent?: FernieHeaderAccent;
	branded?: boolean;
	color?: string;
	icon?: string;
	mono?: boolean;
	pillLabel?: string;
	toolName?: string;
}

const LegacyHeader = ({ color, icon, mono = true, toolName }: FernieCardHeaderProps) => (
	<Box sx={{ alignItems: "center", display: "flex", gap: 0.75, px: 1.5, pt: 1.25 }}>
		<Icon sx={{ color, fontSize: 16 }}>{icon}</Icon>
		<Typography
			sx={{
				color: "text.secondary",
				fontFamily: mono ? "'JetBrains Mono', monospace" : undefined,
				fontSize: "0.68rem",
				fontWeight: 500,
				letterSpacing: mono ? "0.02em" : undefined
			}}
		>
			{toolName}
		</Typography>
	</Box>
);

const BrandedHeader = ({ accent, pillLabel, toolName }: FernieCardHeaderProps) => {
	const resolvedAccent: FernieHeaderAccent = accent ?? "primary";
	const resolvedLabel = pillLabel ?? toolName ?? "";

	return (
		<Box sx={{ alignItems: "center", display: "flex", gap: 0.75, px: 2, pt: 1.5 }}>
			<Box sx={{ display: "flex" }}>
				<HeaderMark />
			</Box>
			<FernieName color="text.primary" fontSize="0.82rem" />
			<Box
				sx={{
					backgroundColor: (theme) => alpha(theme.palette.success.main, 0.12),
					borderRadius: "6px",
					color: "success.dark",
					fontSize: "0.55rem",
					fontWeight: 700,
					letterSpacing: "0.08em",
					px: 0.6,
					py: 0.2
				}}
			>
				BETA
			</Box>
			<Box sx={{ flex: 1 }} />
			{resolvedLabel && (
				<Box
					sx={{
						border: "1px solid",
						borderColor: (theme) => alpha(theme.palette[resolvedAccent].main, 0.4),
						borderRadius: "999px",
						color: `${resolvedAccent}.main`,
						fontSize: "0.6rem",
						fontWeight: 700,
						letterSpacing: "0.07em",
						px: 1,
						py: 0.35,
						textTransform: "uppercase"
					}}
				>
					{resolvedLabel}
				</Box>
			)}
		</Box>
	);
};

const FernieCardHeader = (props: FernieCardHeaderProps) =>
	props.branded ? <BrandedHeader {...props} /> : <LegacyHeader {...props} />;

export default FernieCardHeader;
